import React, { useState, useEffect, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface CustomTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  maxHeight?: string;
}

export const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  value,
  onChange,
  maxHeight = "200px",
}) => {
  const isMobile = useIsMobile();
  const { locale: contextLocale } = useLanguage();

  const hourFormat = localStorage.getItem('hourFormat');
  const locale = hourFormat === '12' ? 'en' : (hourFormat === '24' ? 'fr' : contextLocale);
  
  const [inputValue, setInputValue] = useState(value);
  const [ampm, setAmpm] = useState<"AM" | "PM">("AM");
  const [open, setOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const hoursRef = useRef<HTMLDivElement | null>(null);
  const minsRef = useRef<HTMLDivElement | null>(null);

  // Mettre à jour input + AM/PM quand value change
  useEffect(() => {
    if (!value) {
      setInputValue("");
      return;
    }
    const [h, m] = value.split(":").map(Number);
    if (locale === "en") {
      setAmpm(h < 12 ? "AM" : "PM");
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      setInputValue(`${hour12}:${m.toString().padStart(2, "0")}`);
    } else {
      setInputValue(value);
    }
  }, [value, locale]);

  // Minutes toutes les 5 min + minute actuelle si pas dedans
  let minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, "0"));
  const selectedMinute = value ? value.split(":")[1] : null;
  if (selectedMinute && !minutes.includes(selectedMinute)) {
    minutes = [...minutes, selectedMinute].sort((a, b) => parseInt(a) - parseInt(b));
  }

  // Heures affichées
  const buildHours = () => {
    if (locale === "fr") {
      return Array.from({ length: 24 }, (_, i) => ({
        display: i.toString().padStart(2, "0") + "h",
        value: i.toString().padStart(2, "0"),
      }));
    } else {
      return Array.from({ length: 12 }, (_, i) => {
        const hour12 = i + 1;
        return { display: hour12.toString(), value: hour12.toString() };
      });
    }
  };
  const hours = buildHours();

  // Valeurs sélectionnées (pour highlight + scroll)
  const [hSel, mSel] = value ? value.split(":").map(Number) : [null, null];
  let selectedHour: string | null = null;
  if (hSel !== null) {
    if (locale === "en") {
      selectedHour = (hSel % 12 === 0 ? 12 : hSel % 12).toString(); // 1..12
    } else {
      selectedHour = hSel.toString().padStart(2, "0"); // 00..23
    }
  }
  const selectedMinuteNorm = mSel !== null ? mSel.toString().padStart(2, "0") : null;

  // Affichage bouton principal
  const displayValue = () => {
    if (!value) return locale === 'en' ? "H:MM" : "HH:MM";
    const [h, m] = value.split(":").map(Number);
    if (locale === "en") {
      const suffix = h < 12 ? "AM" : "PM";
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      return `${hour12}:${m.toString().padStart(2, "0")} ${suffix}`;
    }
    return value;
  };

  // Scroll automatique
  useEffect(() => {
    if (!open || !value) return;
    requestAnimationFrame(() => {
      if (hoursRef.current && selectedHour) {
        const btn = hoursRef.current.querySelector<HTMLButtonElement>(`[data-hour='${selectedHour}']`);
        btn?.scrollIntoView({ block: "center" });
      }
      if (minsRef.current && selectedMinuteNorm) {
        const btn = minsRef.current.querySelector<HTMLButtonElement>(`[data-minute='${selectedMinuteNorm}']`);
        btn?.scrollIntoView({ block: "center" });
      }
    });
  }, [open, value, locale, selectedHour, selectedMinuteNorm]);

  // Scroll sur roue
  const onWheelScroll = (e: React.WheelEvent, containerRef: React.RefObject<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;
    const delta = e.deltaY;
    const atTop = el.scrollTop === 0 && delta < 0;
    const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight && delta > 0;
    if (!atTop && !atBottom) {
      el.scrollTop += delta;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Changement input texte
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length > 4) val = val.substring(0, 4);
    if (val.length >= 3) val = `${val.slice(0, val.length - 2)}:${val.slice(-2)}`;
    setInputValue(val);
  };

  // Validation et conversion
  const validateAndSubmit = (forcedAmPm?: "AM" | "PM") => {
    if (!inputValue) {
      onChange("00:00");
      return;
    }

    let [h, m] = inputValue.split(":");
    let hour = parseInt(h) || 0;
    let minute = parseInt(m) || 0;

    const effectiveAmPm = forcedAmPm ?? ampm;

    if (locale === "en") {
      if (effectiveAmPm === "PM" && hour < 12) hour += 12;
      if (effectiveAmPm === "AM" && hour === 12) hour = 0;
    }

    hour = Math.min(23, Math.max(0, hour));
    minute = Math.min(59, Math.max(0, minute));

    const formattedTime = `${hour.toString().padStart(2, "0")}:${minute
      .toString()
      .padStart(2, "0")}`;

    if (locale === "en") {
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      setInputValue(`${hour12}:${minute.toString().padStart(2, "0")}`);
    } else {
      setInputValue(formattedTime);
    }

    onChange(formattedTime);
  };

  if (isMobile) {
    return (
      <Input
        type="time"
        value={value}
        onChange={(e) => {
          const newTime = e.target.value;
          setInputValue(newTime);
          onChange(newTime);
        }}
        className="w-full appearance-none rounded-md border px-3 py-2"
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal flex items-center gap-2"
        >
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>{displayValue()}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-0 bg-gray-50" align="start">
        <div className="p-2 space-y-2">
          <Input
            ref={inputRef}
            type="text"
            placeholder={locale === "en" ? "H:MM" : "HH:MM"}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={() => validateAndSubmit()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                validateAndSubmit();
                setOpen(false);
              }
            }}
            className="w-full text-center"
          />

          {locale === "en" && (
            <div className="flex justify-center gap-2">
              <Button
                variant={ampm === "AM" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setAmpm("AM");
                  validateAndSubmit("AM");
                }}
              >
                AM
              </Button>
              <Button
                variant={ampm === "PM" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setAmpm("PM");
                  validateAndSubmit("PM");
                }}
              >
                PM
              </Button>
            </div>
          )}

          <div className="flex" style={{ height: maxHeight }}>
            {/* Heures */}
            <div
              ref={hoursRef}
              className="w-1/2 pr-1 overflow-y-auto overscroll-contain"
              style={{ maxHeight }}
              onWheel={(e) => onWheelScroll(e, hoursRef)}
            >
              <div className="flex flex-col">
                {hours.map((h) => (
                  <Button
                    key={h.value}
                    data-hour={h.value}
                    variant="ghost"
                    className={`w-full justify-start px-2 py-1 text-sm hover:bg-gray-100 block ${
                      h.value === selectedHour ? "bg-gray-200" : ""
                    }`}
                    onClick={() => {
                    const currentMinute = inputValue.split(":")[1] || "00";
                    let newHour = parseInt(h.value);

                    if (locale === "en") {
                      if (ampm === "PM" && newHour < 12) newHour += 12;
                      if (ampm === "AM" && newHour === 12) newHour = 0;
                    }

                    const finalTime = `${newHour.toString().padStart(2, "0")}:${currentMinute}`;
                    onChange(finalTime);

                    // 💡 mettre à jour inputValue avec le format affiché
                    if (locale === "en") {
                      const hour12 = newHour % 12 === 0 ? 12 : newHour % 12;
                      setInputValue(`${hour12}:${currentMinute}`);
                    } else {
                      setInputValue(finalTime);
                    }
                  }}
                  >
                    {h.display}
                  </Button>
                ))}
              </div>
            </div>

            <Separator orientation="vertical" className="mx-1" />

            {/* Minutes */}
            <div
              ref={minsRef}
              className="w-1/2 pl-1 overflow-y-auto overscroll-contain"
              style={{ maxHeight }}
              onWheel={(e) => onWheelScroll(e, minsRef)}
            >
              <div className="flex flex-col">
                {minutes.map((minute) => (
                  <Button
                    key={minute}
                    data-minute={minute}
                    variant="ghost"
                    className={`w-full justify-start px-2 py-1 text-sm hover:bg-gray-100 block ${
                      minute === selectedMinuteNorm ? "bg-gray-200" : ""
                    }`}
                    onClick={() => {
                      const [h] = value.split(":");
                      const finalTime = `${h}:${minute}`;
                      onChange(finalTime);

                      // 💡 pareil, mettre à jour inputValue
                      if (locale === "en") {
                        const hour12 = (parseInt(h) % 12 === 0 ? 12 : parseInt(h) % 12);
                        setInputValue(`${hour12}:${minute}`);
                      } else {
                        setInputValue(finalTime);
                      }

                      setOpen(false);
                    }}
                  >
                    {minute}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
